import { isFuture } from 'date-fns'
import { Instructor } from '@mc/api'
import { Badge, Icon, Tile, TileCaption, TileImage } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { useStaticData } from '@mc/static-data-react'
import Image from 'components/LegacyImage'
import { episodePath } from 'libs/paths'
import { Chapter } from 'types/data'
import { EpisodeStaticData } from '../../[episodeSlug].page'
import { useEpisodeVideoModal } from './EpisodeListVideoContext'
import { trackEpisodeClick, trackEpisodeGemClick } from './events'
import classes from './EpisodeList.module.scss'

interface EpisodeTileProps {
  episode: Chapter
}

export const EpisodeTile = ({ episode }: EpisodeTileProps) => {
  const { t } = useTranslation('@mc/next')

  const { course, episode: pageEpisode } = useStaticData<EpisodeStaticData>()

  const { onOpen } = useEpisodeVideoModal()

  const shouldPlayVideo = !!(
    episode.teaser?.brightcoveVideoId || episode.teaser?.mediaUuid
  )
  const shouldShowBadge = episode.availableAt
    ? isFuture(new Date(episode.availableAt))
    : false

  const instructor = episode.instructors?.[0]
  const shouldShowInstructor = episode.instructors?.length === 1

  return (
    <div className='mc-mb-4 mc-mb-md-0'>
      <div
        className={`mc-corners--lg mc-overflow--hidden mc-mb-3 ${classes.imgContainer}`}
      >
        <Tile
          onClick={() => {
            if (shouldPlayVideo) {
              onOpen(episode)
              trackEpisodeGemClick({
                episodeSlug: pageEpisode.slug as string,
                seriesSlug: course.slug as string,
                contentId: `${episode?.id}_${
                  episode?.teaser?.brightcoveVideoId ||
                  episode?.teaser?.mediaUuid
                }`,
                contentTitle: episode?.title || '',
              })
            }
          }}
        >
          {shouldPlayVideo && (
            <TileCaption position='center center'>
              <Icon
                kind='play-circle'
                className='mc-icon--6 d-none d-md-inline'
              />
              <Icon kind='play-circle' className='mc-icon--8 d-md-none' />
            </TileCaption>
          )}
          <TileImage
            imageUrl={episode.videoThumbUrl as string}
            data-testid={`episode-${episode.number}-img`}
          />
        </Tile>
        <div className={`position-absolute mc-m-3 ${classes.badgeContainer}`}>
          {shouldShowBadge && (
            <Badge className={`mc-text-x-small ${classes.badge}`}>
              {t('episode.episodeList.episode.comingSoon')}
            </Badge>
          )}
        </div>
      </div>

      <a
        className={`mc-text--bold ${classes.episodeTitle}`}
        href={episodePath(course.slug, episode.slug)}
        onClick={() =>
          trackEpisodeClick({
            seriesSlug: course.slug as string,
            episodeSlug: episode.slug as string,
          })
        }
      >
        {t('episode.episodeList.episode.title.default', {
          episodeNumber: episode.number,
          episodeTitle: episode.title,
        })}
      </a>
      <p className='mc-opacity--muted d-none d-md-block'>{episode.abstract}</p>
      <p className='mc-text-color--medium d-block d-md-none'>
        {episode.abstract}
      </p>
      {shouldShowInstructor && (
        <InstructorDescription instructor={instructor as Instructor} />
      )}
    </div>
  )
}

interface InstructorDescriptionProp {
  instructor: Instructor
}

const InstructorDescription = ({ instructor }: InstructorDescriptionProp) => {
  const { t } = useTranslation('@mc/next')
  const { headshotUrl, headshotUrlAltText, name, bio } =
    instructor as unknown as Instructor

  return (
    <div className='d-none d-md-flex align-items-center mc-mt-2'>
      <div className='col-auto mc-px-0'>
        <Image
          className='mc-corners--circle'
          src={headshotUrl as string}
          alt={headshotUrlAltText}
          width={29}
          height={29}
        />
      </div>
      <div className='col-10 mc-my-auto mc-text-x-small'>
        <span>
          {t('episode.episodeList.episode.instructor', {
            instructorName: name,
            instructorBio: bio,
          })}
        </span>
      </div>
    </div>
  )
}
